import { ButtonHTMLAttributes, forwardRef, ReactNode, RefObject, useImperativeHandle, useRef } from 'react';
import { classNames } from 'primereact/utils';
import {
  Tooltip,
  TOOLTIP_SURFACE,
  TOOLTIP_SURFACE_BOTTOM,
  TOOLTIP_SURFACE_SIDE,
  TOOLTIP_SURFACE_SIDE_LEFT,
} from '@repo/atomic-components';

type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

const TOOLTIP_SURFACE_BY_POSITION: Record<TooltipSide, string> = {
  top: TOOLTIP_SURFACE,
  bottom: TOOLTIP_SURFACE_BOTTOM,
  right: TOOLTIP_SURFACE_SIDE,
  left: TOOLTIP_SURFACE_SIDE_LEFT,
};

interface HeaderIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ReactNode;
  /** Renders a tooltip beside the button and doubles as the aria-label. */
  tooltip: string;
  /** Optional second line shown under the tooltip title on hover. */
  tooltipDescription?: string;
  /** Tooltip side. Defaults to `"bottom"`; use `"left"` at the right edge of a narrow panel. */
  tooltipPosition?: TooltipSide;
  /** Milliseconds of hover before the tooltip shows. Defaults to showing immediately. */
  tooltipShowDelay?: number;
  /** Subtle "toggled on" background — same shade as hover. */
  active?: boolean;
  /** Hides the tooltip, e.g. while a menu the button opened covers it. The aria-label stays. */
  hideTooltip?: boolean;
}

/** Shared 32×32 header icon button: no background at rest, grey hover, icon darkens on hover. */
export const HeaderIconButton = forwardRef<HTMLButtonElement, HeaderIconButtonProps>(function HeaderIconButton(
  {
    icon,
    tooltip,
    tooltipDescription,
    tooltipPosition = 'bottom',
    tooltipShowDelay,
    active,
    hideTooltip,
    className,
    disabled,
    onClick,
    ...buttonProps
  },
  ref,
) {
  // The tooltip needs a ref object; the forwarded ref may be a callback.
  const targetRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => targetRef.current as HTMLButtonElement, []);

  return (
    <>
      <button
        ref={targetRef}
        type="button"
        aria-label={tooltip}
        aria-description={tooltipDescription}
        // Uses aria-disabled instead of the HTML attribute so the browser still fires
        // hover events, which the tooltip relies on to appear.
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
        className={classNames(
          'text-theme-text-grey hover:bg-theme-border-grey/70 hover:text-theme-black focus-visible:outline-fletchers-green-500 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 transition-colors',
          active && 'bg-theme-border-grey/70 text-theme-black',
          disabled && 'hover:text-theme-text-grey cursor-not-allowed opacity-60 hover:bg-transparent',
          className,
        )}
        {...buttonProps}
      >
        {icon}
      </button>
      {!hideTooltip && (
        <Tooltip
          target={targetRef as RefObject<HTMLElement>}
          title={tooltipDescription ? tooltip : undefined}
          description={tooltipDescription}
          content={tooltipDescription ? undefined : tooltip}
          position={tooltipPosition}
          showDelay={tooltipShowDelay}
          className={TOOLTIP_SURFACE_BY_POSITION[tooltipPosition]}
          baseZIndex={10000}
        />
      )}
    </>
  );
});
